/**
 * ArtifactBrowser — demoted file tree in <details>/collapsed by default
 * (RAISE-16886 T8, AC5).
 *
 * Config toggle filters .yaml from tree. Sheet opens ArtifactViewer on
 * node click.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGovernanceArtifacts } from "@/hooks/useGovernanceArtifacts";
import { GovernanceTree } from "@/components/project/GovernanceTree";
import { ArtifactViewer } from "@/components/project/ArtifactViewer";
import { buildTree } from "@/lib/governanceTree";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import type { GovernanceArtifactItem } from "@/hooks/useGovernanceArtifacts";

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface ArtifactBrowserProps {
  projectSlug: string;
}

export function ArtifactBrowser({ projectSlug }: ArtifactBrowserProps) {
  const { t } = useTranslation();
  const [showConfig, setShowConfig] = useState(false);
  const [selectedArtifact, setSelectedArtifact] =
    useState<GovernanceArtifactItem | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const { artifacts } = useGovernanceArtifacts(projectSlug);

  // Filter .yaml/.yml when showConfig is false
  const visibleArtifacts = showConfig
    ? artifacts
    : artifacts.filter(
        (a) => !a.path.endsWith(".yaml") && !a.path.endsWith(".yml"),
      );

  const tree = buildTree(visibleArtifacts);

  function handleSelect(artifact: GovernanceArtifactItem) {
    setSelectedArtifact(artifact);
    setSheetOpen(true);
  }

  return (
    <>
      {/* Native <details> — starts collapsed (no 'open' attribute) */}
      <details className="group">
        <summary className="flex items-center justify-between cursor-pointer select-none list-none py-2 px-1 rounded-md hover:bg-muted/50 transition-colors">
          <span className="text-sm font-medium">
            {t("projectPortal.governance.artifactBrowser.title", {
              defaultValue: "Artifact Browser",
            })}
          </span>
          <span className="text-xs text-muted-foreground ml-2">
            ({artifacts.length}{" "}
            {t("projectPortal.governance.artifactBrowser.files", {
              defaultValue: "files",
            })}
            )
          </span>
        </summary>

        {/* Config toggle */}
        <div className="mt-2 mb-3 flex items-center gap-2 px-1">
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={showConfig}
              onChange={(e) => setShowConfig(e.target.checked)}
              className="rounded"
            />
            {t("projectPortal.governance.artifactBrowser.showConfig", {
              defaultValue: "Show config files (.yaml)",
            })}
          </label>
        </div>

        {/* Tree */}
        {tree.length > 0 ? (
          <GovernanceTree tree={tree} onSelect={handleSelect} />
        ) : (
          <p className="text-sm text-muted-foreground py-2 px-1">
            {t("projectPortal.governance.artifactBrowser.empty", {
              defaultValue: "No artifacts to display.",
            })}
          </p>
        )}
      </details>

      {/* Detail sheet */}
      <Sheet
        open={sheetOpen}
        onOpenChange={(open) => {
          if (!open) setSheetOpen(false);
        }}
      >
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {t("projectPortal.governance.artifactBrowser.sheetTitle", {
                defaultValue: "Artifact Detail",
              })}
            </SheetTitle>
          </SheetHeader>
          {selectedArtifact && (
            <div className="mt-4">
              <ArtifactViewer artifact={selectedArtifact} />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
